import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Accordion,
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { IconEdit, IconNotes, IconPlus, IconRefresh, IconSearch, IconTrash } from '@/components/icons'
import { ProjectIcon } from '@/components/sidebar/ProjectIcon'
import { ActionButton, AppModal, EmptyState, PageCenterLoader, PageShell, PageToolbar } from '@/components/ui'
import { api, ApiError } from '@/lib/api'
import type { MacroRow, ProjectRow } from '@/types/api'

type ProjectMacrosBundle = {
  project: ProjectRow
  macros: MacroRow[]
  loadError: string | null
}

function macroCountLabel(n: number): string {
  const m = n % 10
  const k = n % 100
  if (m === 1 && k !== 11) return `${n} макрос`
  if (m >= 2 && m <= 4 && (k < 12 || k > 14)) return `${n} макроса`
  return `${n} макросов`
}

export function MacrosPage() {
  const [loading, setLoading] = useState(true)
  const [bundles, setBundles] = useState<ProjectMacrosBundle[]>([])
  const [search, setSearch] = useState('')
  const [accordionValue, setAccordionValue] = useState<string[]>([])
  const accordionInited = useRef(false)
  /** Инвалидация зависшего loadAll при уходе со страницы (Strict Mode). */
  const loadGenRef = useRef(0)

  const [formOpen, setFormOpen] = useState(false)
  const [formProject, setFormProject] = useState<ProjectRow | null>(null)
  const [editMacro, setEditMacro] = useState<MacroRow | null>(null)
  const [formName, setFormName] = useState('')
  const [formContent, setFormContent] = useState('')
  const [saving, setSaving] = useState(false)

  const loadAll = useCallback(async () => {
    const gen = ++loadGenRef.current
    setLoading(true)
    const done = () => {
      if (gen === loadGenRef.current) setLoading(false)
    }
    try {
      const projects = await api.projects()
      if (gen !== loadGenRef.current) return
      const list = Array.isArray(projects) ? projects : []
      const results = await Promise.all(
        list.map(async (project) => {
          try {
            const macros = await api.projectMacros(project.id)
            return {
              project,
              macros: Array.isArray(macros) ? macros : [],
              loadError: null as string | null,
            }
          } catch (e) {
            return {
              project,
              macros: [] as MacroRow[],
              loadError: e instanceof ApiError ? e.message : 'Ошибка загрузки',
            }
          }
        }),
      )
      if (gen !== loadGenRef.current) return
      results.sort((a, b) => (a.project.display_order ?? 0) - (b.project.display_order ?? 0))
      setBundles(results)
      if (!accordionInited.current && results.length > 0) {
        accordionInited.current = true
        setAccordionValue(results.map((r) => r.project.id))
      }
    } catch (e) {
      if (gen === loadGenRef.current) {
        showNotification({
          title: 'Ошибка',
          message: e instanceof ApiError ? e.message : 'Не удалось загрузить проекты',
          color: 'red',
        })
        setBundles([])
      }
    } finally {
      done()
    }
  }, [])

  useEffect(() => {
    void loadAll()
    return () => {
      loadGenRef.current++
    }
  }, [loadAll])

  const q = search.trim().toLowerCase()

  const visibleBundles = useMemo(() => {
    if (!q) return bundles
    return bundles
      .map(({ project, macros, loadError }) => {
        const nameHit =
          (project.name || '').toLowerCase().includes(q) ||
          (project.slug || '').toLowerCase().includes(q)
        const macroHits = macros.filter(
          (m) =>
            (m.name || '').toLowerCase().includes(q) || (m.content || '').toLowerCase().includes(q),
        )
        const showMacros = nameHit ? macros : macroHits
        return {
          project,
          macros: showMacros,
          loadError,
          _empty: showMacros.length === 0 && !nameHit,
        }
      })
      .filter((b) => !b._empty)
      .map(({ project, macros, loadError }) => ({ project, macros, loadError }))
  }, [bundles, q])

  const totalMacros = useMemo(() => bundles.reduce((n, b) => n + b.macros.length, 0), [bundles])

  const openCreate = (project: ProjectRow) => {
    setFormProject(project)
    setEditMacro(null)
    setFormName('')
    setFormContent('')
    setFormOpen(true)
  }

  const openEdit = (project: ProjectRow, macro: MacroRow) => {
    setFormProject(project)
    setEditMacro(macro)
    setFormName(macro.name || '')
    setFormContent(macro.content || '')
    setFormOpen(true)
  }

  const submitForm = async () => {
    const name = formName.trim()
    if (!name) {
      showNotification({ title: 'Название', message: 'Укажите название макроса.', color: 'yellow' })
      return
    }
    if (!formProject) return
    setSaving(true)
    try {
      if (editMacro) {
        await api.macroUpdate(formProject.id, editMacro.id, {
          name,
          content: formContent,
        })
      } else {
        await api.macroCreate(formProject.id, { name, content: formContent })
      }
      setFormOpen(false)
      await loadAll()
      showNotification({ title: 'Сохранено', message: '', color: 'green' })
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось сохранить',
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = (project: ProjectRow, macro: MacroRow) => {
    openConfirmModal({
      title: 'Удалить макрос?',
      children: (
        <Text size="sm">
          «{macro.name}» будет удалён. Его нельзя будет выбрать в избранных ответах.
        </Text>
      ),
      labels: { confirm: 'Удалить', cancel: 'Отмена' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await api.macroDelete(project.id, macro.id)
          await loadAll()
          showNotification({ title: 'Удалено', message: '', color: 'green' })
        } catch (e) {
          showNotification({
            title: 'Ошибка',
            message: e instanceof ApiError ? e.message : 'Не удалось удалить',
            color: 'red',
          })
        }
      },
    })
  }

  const expandAll = () => setAccordionValue(visibleBundles.map((b) => b.project.id))
  const collapseAll = () => setAccordionValue([])

  return (
    <PageShell>
      <Stack gap="md">
        <PageToolbar
          leading={
            <Box>
              <Group gap="xs" wrap="nowrap">
                <IconNotes size={20} stroke={1.6} />
                <Text fw={700} size="lg">
                  Макросы
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                Быстрые ответы по проектам для операторов.
              </Text>
            </Box>
          }
          trailing={
            <Button
              leftSection={<IconRefresh size={16} stroke={1.5} />}
              variant="default"
              onClick={() => void loadAll()}
              disabled={loading}
            >
              Обновить
            </Button>
          }
        />

        <Card withBorder padding="md" radius="md">
          <PageToolbar
            leading={
            <TextInput
              label="Поиск"
              placeholder="Проект, название или текст макроса…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftSection={<IconSearch size={14} stroke={1.5} />}
              style={{ flex: '1 1 260px', minWidth: 200 }}
            />
            }
            trailing={
            <Group gap="xs">
              <Text size="xs" c="dimmed" style={{ alignSelf: 'center' }}>
                Проектов: {bundles.length} · макросов: {totalMacros}
              </Text>
              <Button size="compact-sm" variant="default" onClick={expandAll}>
                Развернуть все
              </Button>
              <Button size="compact-sm" variant="default" onClick={collapseAll}>
                Свернуть все
              </Button>
            </Group>
            }
          />
        </Card>

        {loading ? (
          <PageCenterLoader label="Загрузка" minHeight={200} />
        ) : bundles.length === 0 ? (
          <EmptyState
            icon={<IconNotes size={32} stroke={1.5} />}
            title="Нет проектов"
            description="Когда появятся проекты в workspace, здесь можно будет завести макросы."
          />
        ) : visibleBundles.length === 0 ? (
          <EmptyState
            title="Ничего не найдено"
            description="Измените запрос поиска."
            icon={<IconNotes size={32} stroke={1.5} />}
          />
        ) : (
          <Accordion
            multiple
            variant="separated"
            radius="md"
            value={accordionValue}
            onChange={setAccordionValue}
          >
            {visibleBundles.map(({ project, macros, loadError }) => (
              <Accordion.Item key={project.id} value={project.id}>
                <Accordion.Control>
                  <Group justify="space-between" wrap="nowrap" gap="sm" pr="xs">
                    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                      <Box
                        style={{
                          borderRadius: 10,
                          padding: 6,
                          background: 'var(--mantine-color-body)',
                          border: '1px solid var(--mantine-color-default-border)',
                        }}
                      >
                        <ProjectIcon project={project} boxSize={22} />
                      </Box>
                      <Box style={{ minWidth: 0 }}>
                        <Text fw={600} size="sm" truncate>
                          {project.name || project.slug}
                        </Text>
                        <Text size="xs" c="dimmed" truncate>
                          {macroCountLabel(macros.length)}
                          {loadError ? ' · ошибка загрузки' : ''}
                        </Text>
                      </Box>
                    </Group>
                    <Badge size="sm" variant="light" color="gray">
                      {macros.length}
                    </Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  {loadError ? (
                    <Alert color="red" variant="light" title="Не удалось загрузить">
                      {loadError}
                    </Alert>
                  ) : null}
                  <Group justify="flex-end" mb="sm">
                    <Button
                      size="xs"
                      leftSection={<IconPlus size={14} stroke={1.5} />}
                      onClick={() => openCreate(project)}
                    >
                      Макрос в этом проекте
                    </Button>
                  </Group>
                  {macros.length === 0 && !loadError ? (
                    <Text size="sm" c="dimmed" py="md" ta="center">
                      Пока нет макросов. Добавьте первый — он появится в меню вставки в ответе.
                    </Text>
                  ) : (
                    <Stack gap="sm">
                      {macros.map((m) => (
                        <Card key={m.id} withBorder padding="sm" radius="md">
                          <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
                            <Box style={{ minWidth: 0, flex: 1 }}>
                              <Text fw={600} size="sm">
                                {m.name}
                              </Text>
                              <Text size="sm" c="dimmed" lineClamp={4} style={{ whiteSpace: 'pre-wrap' }}>
                                {m.content?.trim() ? m.content : '—'}
                              </Text>
                            </Box>
                            <Group gap={4} wrap="nowrap">
                              <ActionIcon
                                variant="subtle"
                                aria-label="Изменить"
                                onClick={() => openEdit(project, m)}
                              >
                                <IconEdit size={18} stroke={1.5} />
                              </ActionIcon>
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                aria-label="Удалить"
                                onClick={() => confirmDelete(project, m)}
                              >
                                <IconTrash size={18} stroke={1.5} />
                              </ActionIcon>
                            </Group>
                          </Group>
                        </Card>
                      ))}
                    </Stack>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        )}
      </Stack>

      <AppModal
        opened={formOpen}
        onClose={() => setFormOpen(false)}
        title={
          editMacro
            ? `Макрос: ${formProject?.name || ''}`
            : `Новый макрос — ${formProject?.name || ''}`
        }
        size="lg"
        footer={
          <>
            <ActionButton action="cancel" onClick={() => setFormOpen(false)} />
            <ActionButton action="save" loading={saving} onClick={() => void submitForm()} />
          </>
        }
      >
        <Stack gap="sm">
          <TextInput
            label="Название"
            placeholder="Например: Приветствие"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            required
          />
          <Textarea
            label="Текст"
            placeholder="Текст для вставки в ответ (поддерживаются переменные шаблонов, если настроены)"
            value={formContent}
            onChange={(e) => setFormContent(e.target.value)}
            minRows={6}
            autosize
            maxRows={16}
          />
        </Stack>
      </AppModal>
    </PageShell>
  )
}
